import {memo} from 'react';
import {useI18n} from '@shopify/react-i18n';
import classNames from 'classnames';
import {InlineStack, BlockStack} from '@shopify/polaris';
import {TranslatableResourceType} from 'types';
import {sentenceCase} from 'change-case';

import type {
  TranslatableField,
  TranslatableResourceLayoutFormProps,
} from '../types';
import {
  groupCookieBannerFields,
  groupThemeAssets,
  splitResourcesBySpecificTypes,
} from '../../utils';

import styles from './TranslatableResourceTable.scss';
import {RowCellMarkup, Table} from './components';

function TranslatableResourceTable({
  resourceType,
  defaultContentLocaleName,
  translatedContentLocaleName,
  fields,
  setField,
  disabled,
  machineTranslationInProgress,
  market,
  primaryLocale,
  locale,
}: Omit<TranslatableResourceLayoutFormProps, 'loading'> & {
  resourceType: TranslatableResourceType;
}) {
  const [i18n] = useI18n();

  const showContextualizedTable = locale.locale !== primaryLocale.locale;

  const rowHeaderForTranslatableFields = [
    i18n.translate('TranslatableResourceTable.field'),
    defaultContentLocaleName,
    market ? (
      <InlineStack wrap={false} gap="400" blockAlign="center">
        <span>{translatedContentLocaleName}</span>
      </InlineStack>
    ) : (
      translatedContentLocaleName
    ),
  ];

  let currentGroup: string | undefined;
  let groupIndex = 0;

  function buildRow(
    fieldProps: TranslatableField & {
      relatedFields?: TranslatableField[];
      defaultContentLocaleName: string;
      translatedContentLocaleName: string;
    },
  ) {
    if (fieldProps.hidden) {
      return null;
    }

    if (fieldProps.groupKey) {
      if (currentGroup === fieldProps.groupKey) {
        groupIndex++;
      } else {
        currentGroup = fieldProps.groupKey;
        groupIndex = 0;
      }
    } else {
      currentGroup = undefined;
    }

    return (
      <RowCellMarkup
        key={`${fieldProps.gidKey}-RowCellMarkup`}
        setField={setField}
        group={
          fieldProps.groupKey && fieldProps.groupSize
            ? {
                currentIndex: groupIndex,
                size: fieldProps.groupSize,
              }
            : undefined
        }
        disabled={disabled}
        {...fieldProps}
      />
    );
  }

  const translatableFields = fields.filter(
    ({isOnlyMarketLocalizable, isContextualizedThemeContent}) =>
      !isOnlyMarketLocalizable && !isContextualizedThemeContent,
  );
  const marketLocalizableFields = fields.filter(
    ({isOnlyMarketLocalizable}) => isOnlyMarketLocalizable,
  );
  const contextualizedThemeFields = fields.filter(
    ({isContextualizedThemeContent}) => isContextualizedThemeContent,
  );

  const translatableFieldsTable = () => {
    if (
      resourceType === TranslatableResourceType.OnlineStoreThemeLocaleContent
    ) {
      const groupedThemeAssetFields = groupThemeAssets(fields);
      return Object.keys(groupedThemeAssetFields).map((key) => (
        <Table
          key={key}
          title={sentenceCase(key)}
          headerRows={rowHeaderForTranslatableFields}
        >
          {groupedThemeAssetFields[key].map((field) =>
            buildRow({
              ...field,
              defaultContentLocaleName,
              translatedContentLocaleName,
            }),
          )}
        </Table>
      ));
    } else if (resourceType === TranslatableResourceType.CookieBanner) {
      const groupedCookieBannerFields = groupCookieBannerFields(fields);
      return Object.keys(groupedCookieBannerFields).map((key) => (
        <Table
          key={key}
          title={i18n.translate(`FieldGroupsLabels.cookie_banner_${key}`)}
          headerRows={rowHeaderForTranslatableFields}
        >
          {groupedCookieBannerFields[key].map((field) =>
            buildRow({
              ...field,
              defaultContentLocaleName,
              translatedContentLocaleName,
            }),
          )}
        </Table>
      ));
    }

    const tables = [];

    const {mainResource, productOptions, metafields, filterGroups} =
      splitResourcesBySpecificTypes(translatableFields, market);

    let headerShown = false;

    if (mainResource.length) {
      tables.push(
        <Table
          key={resourceType}
          title={i18n.translate(`ResourceTypes.${resourceType}`, {count: 1})}
          headerRows={headerShown ? undefined : rowHeaderForTranslatableFields}
        >
          {mainResource.map((field) =>
            buildRow({
              ...field,
              defaultContentLocaleName,
              translatedContentLocaleName,
            }),
          )}
        </Table>,
      );
      headerShown = true;
    }

    if (productOptions.length) {
      tables.push(
        <Table
          key={TranslatableResourceType.ProductOption}
          title={i18n.translate(
            `ResourceTypes.${TranslatableResourceType.ProductOption}`,
            {
              count: productOptions.length,
            },
          )}
          headerRows={headerShown ? undefined : rowHeaderForTranslatableFields}
        >
          {productOptions.map((field) =>
            buildRow({
              ...field,
              defaultContentLocaleName,
              translatedContentLocaleName,
            }),
          )}
        </Table>,
      );
      headerShown = true;
    }

    if (metafields.length) {
      tables.push(
        <Table
          key={TranslatableResourceType.Metafield}
          title={i18n.translate(
            `ResourceTypes.${TranslatableResourceType.Metafield}`,
            {
              count: metafields.length,
            },
          )}
          headerRows={headerShown ? undefined : rowHeaderForTranslatableFields}
        >
          {metafields.map((field) =>
            buildRow({
              ...field,
              defaultContentLocaleName,
              translatedContentLocaleName,
            }),
          )}
        </Table>,
      );
      headerShown = true;
    }

    if (filterGroups.length) {
      tables.push(
        <Table
          key={TranslatableResourceType.OnlineStoreFilterSettingGroup}
          title={i18n.translate(
            `ResourceTypes.${TranslatableResourceType.OnlineStoreFilterSettingGroup}`,
            {
              count: filterGroups.length,
            },
          )}
          headerRows={headerShown ? undefined : rowHeaderForTranslatableFields}
        >
          {filterGroups.map((field) =>
            buildRow({
              ...field,
              defaultContentLocaleName,
              translatedContentLocaleName,
            }),
          )}
        </Table>,
      );
      headerShown = true;
    }

    return tables.map((table) => table);
  };

  const marketLocalizableFieldsTable = marketLocalizableFields.length ? (
    <Table
      title={i18n.translate('TranslatableResourceTable.marketSettings.header')}
      subtitle={i18n.translate(
        'TranslatableResourceTable.marketSettings.subHeader',
      )}
    >
      {marketLocalizableFields.map((field) =>
        buildRow({
          ...field,
          defaultContentLocaleName,
          translatedContentLocaleName,
        }),
      )}
    </Table>
  ) : null;

  const rowHeaderForContextualizedThemeTranslatableFields = [
    i18n.translate('TranslatableResourceTable.field'),
    `${primaryLocale.name} (${market?.name})`,
    translatedContentLocaleName,
  ];

  const contextualizedThemeFieldsTable =
    contextualizedThemeFields.length && showContextualizedTable ? (
      <Table
        title={i18n.translate(
          'TranslatableResourceTable.themeContextualization.header',
          {market: market?.name},
        )}
        headerRows={rowHeaderForContextualizedThemeTranslatableFields}
      >
        {contextualizedThemeFields.map((field) =>
          buildRow({
            ...field,
            defaultContentLocaleName,
            translatedContentLocaleName,
          }),
        )}
      </Table>
    ) : null;

  return (
    <div
      className={classNames(
        styles.TranslatableResourceTable,
        machineTranslationInProgress && styles.MachineTranslationInProgress,
      )}
    >
      <BlockStack gap="400">
        {translatableFieldsTable()}
        {marketLocalizableFieldsTable}
        {contextualizedThemeFieldsTable}
      </BlockStack>
    </div>
  );
}

export default memo(TranslatableResourceTable);
